'use strict';

import $ from 'jquery';
import _ from 'underscore';
import mediator from '../js/mediator';
import config from '../js/config';
import utilities from '../js/utilities';
import * as featureFlags from '../utilities/featureFlags';
import { userService } from './CartUserService'

const BUYING_OPTION_BATCH_SIZE = 25;

class ProductPricingService {
  /**
   * Load pricing for the models if needed - using the provided fetch method.
   *
   * @param {Backbone.Model[]} models
   * @param {function} fetch
   * @returns {Number}
   * @private
   */
  _batchRequestModels(models, fetch) {
    // Get all ids that have no buying options
    let partIds = this._getPartIdsToLoadFromModels(models);

    if (partIds.length) {
      // Batch in groups
      let partIdGroups = utilities.chunkArray(partIds, BUYING_OPTION_BATCH_SIZE);

      _.forEach(partIdGroups, fetch);
    }

    return partIds.length;
  }

  /**
   * Load grouped (by region) prices for the models if needed - used for SBO.
   *
   * @param {Backbone.Model[]} models
   * @returns {number}
   */
  requestGroupedPricingIfRequired(models) {
    this._batchRequestModels(models, this._fetchGroupedPricesForPartIds.bind(this));
  }

  /**
   * Load pricing for the models if needed.
   *
   * @param {Backbone.Model[]} models
   * @returns {number}
   */
  requestPricingIfRequired(models) {
    this._batchRequestModels(models, this._fetchPricesForPartIds.bind(this));
  }

  /**
   * Get all ids that have no buying options.
   *
   * @param {Backbone.Model[]} models
   * @returns {string[]}
   * @private
   */
  _getPartIdsToLoadFromModels(models) {
    return _(models)
      .filter(this.doesProductNeedPricing)
      .map(model => (model.get('partId') || '') + '|' + (model.get('manufacturer') || '') + '|' + (model.get('partNumber') || ''));
  }

  /**
   * Check if the product needs pricing - as it does not have any buying options.
   *
   * @param {Backbone.Model} model
   * @returns {boolean}
   */
  doesProductNeedPricing(model) {
    return (model.get('searchResultType') === config.search.resultTypeProduct && (!model.get('pricingDataPopulated')||model.get('primaryBuyingOptionType')==='Backorder'||model.get('primaryBuyingOptionType')==='Quote'));
  }

  /**
   * Apply the region override if present in the query string.
   *
   * @param {string} url
   * @returns {string}
   * @private
   */
  _applyRegionOverride(url) {
    let qs = utilities.deserialize(window.location.search);

    // Check if we need to apply a region override
    if (qs && qs.ro) {
      url += '?ro=' + qs.ro;
    }

    return url;
  }

  /**
   * Fetch the prices for the parts given.
   *
   * @param {string[]} partIds
   * @private
   */
  _fetchPricesForPartIds(partIds) {
    let url = this._applyRegionOverride(config.urls.cart.buyingOptions);

    let callback = customerNumberForSpecificPricing => {
      let promise = $.ajax(url, {
        dataType: 'json',
        method: 'post',
        contentType: 'application/json',
        data: JSON.stringify(utilities.addCustIpToDataObject({
          productDetails: partIds,
          customerNumberForSpecificPricing: customerNumberForSpecificPricing ? customerNumberForSpecificPricing : undefined
        }))
      });

      // Trigger an event which the views will listen to
      promise.done(response => mediator.trigger(config.events.pricing.received, response.data));
    };
    
    callback(null);
  }

  /**
   * Fetch the grouped (by region) prices for the parts given - used for SBO.
   *
   * @param {string[]} partIds
   * @private
   */
  _fetchGroupedPricesForPartIds(partIds) {
    let url = this._applyRegionOverride(config.urls.cart.groupedBuyingOptions);

    let callback = customerNumberForSpecificPricing => {
      let promise = $.ajax(url, {
        dataType: 'json',
        method: 'post',
        contentType: 'application/json',
        data: JSON.stringify(utilities.addCustIpToDataObject({
          productDetails: partIds,
          customerNumberForSpecificPricing : customerNumberForSpecificPricing ? customerNumberForSpecificPricing : undefined
        }))
      });

      // Trigger an event which the views will listen to
      promise.done(response => mediator.trigger(config.events.pricing.groupedBuyingOptionsReceived, response.data));
    };
    
    callback(null);
  }

  /**
   * Find the buying option with the same partId if we have one, otherwise find the same manufacturer and partNumber
   * combination if they exist
   *
   * @param {object[]} parts
   * @param {string} partId
   * @param {string} partNumber
   * @param {string} manufacturer
   * @returns {object}
   * @private
   */
  _findPartInParts(parts, partId, partNumber, manufacturer) {
    return _.find(parts, (p) => {
      if (!p.productDetails) {
        return false;
      }
      return (
        (partId && p.productDetails.partId === partId) ||
        (
          (partNumber && p.productDetails.partNumber === partNumber) &&
          (manufacturer && p.productDetails.manufacturer === manufacturer)
        )
      );
    });
  }

  /**
   * Find a buying option in a list given the details given
   *
   * @param {object[]} parts
   * @param {string} partId
   * @param {string} partNumber
   * @param {string} manufacturer
   * @returns {object}
   */
  findBuyingOption(parts, partId, partNumber, manufacturer) {
    let part = this._findPartInParts(parts, partId, partNumber, manufacturer);

    // Check to see if there were no buying options and force one
    if (part && (!part.buyingOptions || !part.buyingOptions.length)) {
      part.buyingOptions = [{ type: 'None' }];
    }

    return part;
  }

  /**
   * Find a grouped buying option group in a list given the details given
   *
   * @param {object[]} parts
   * @param {string} partId
   * @param {string} partNumber
   * @param {string} manufacturer
   * @returns {object}
   */
  findGroupedBuyingOption(parts, partId, partNumber, manufacturer) {
    let group = this._findPartInParts(parts, partId, partNumber, manufacturer);

    // Check to see if there were no buying options in a region and force one
    if (group && group.regionalBuyingOptions) {
      group.regionalBuyingOptions.forEach(region => {
        if (group && (!region.buyingOptions || !region.buyingOptions.length)) {
          region.buyingOptions = [{ type: 'None' }];
        }
      });
    }

    return group;
  }

}

export default new ProductPricingService();



// WEBPACK FOOTER //
// ./assets/services/ProductPricingService.js